import { inject, Injectable } from '@angular/core';

import { Store } from '@ngxs/store';

import { environment } from '@env/environment';
import { AuditLogService } from '@shared/services/audit-log.service';
import { getApp } from 'firebase/app';
import {
  child,
  Database,
  DatabaseReference,
  DataSnapshot,
  endAt,
  equalTo,
  get,
  getDatabase,
  limitToFirst,
  limitToLast,
  onValue,
  orderByChild,
  orderByKey,
  orderByPriority,
  orderByValue,
  push,
  Query,
  query,
  QueryConstraint,
  ref,
  remove,
  set,
  setPriority,
  setWithPriority,
  startAfter,
  startAt,
  update,
} from 'firebase/database';
import { Observable } from 'rxjs';

type FirebaseOperation = string | number;
type ValueEventCallback = (snapshot: DataSnapshot) => void;

export interface SnapshotAction<T> {
  key: string | null;
  payload: DataSnapshot & { val(): T | null };
  prevKey: string | null;
  type: 'value';
}

export interface FirebaseList<T> {
  readonly query: FirebaseQueryReference;
  valueChanges(): Observable<T[]>;
  snapshotChanges(): Observable<SnapshotAction<T>[]>;
  update(subPath: FirebaseOperation, data: Partial<T>): Promise<void>;
  set(subPath: FirebaseOperation, data: T): Promise<void>;
  push(data: T): ReturnType<typeof push>;
  remove(subPath?: FirebaseOperation): Promise<void>;
}

export interface FirebaseObject<T> {
  readonly query: FirebaseQueryReference;
  valueChanges(): Observable<T | null>;
  snapshotChanges(): Observable<SnapshotAction<T>>;
  update(data: Partial<T>): Promise<void>;
  set(data: T): Promise<void>;
  remove(): Promise<void>;
}

export class FirebaseQueryReference {
  constructor(
    readonly nativeQuery: Query,
    readonly nativeRef: DatabaseReference,
  ) {}

  orderByChild(path: string): FirebaseQueryReference {
    return this.withConstraint(orderByChild(path));
  }

  orderByKey(): FirebaseQueryReference {
    return this.withConstraint(orderByKey());
  }

  orderByPriority(): FirebaseQueryReference {
    return this.withConstraint(orderByPriority());
  }

  orderByValue(): FirebaseQueryReference {
    return this.withConstraint(orderByValue());
  }

  startAt(value: string | number | boolean | null, key?: string): FirebaseQueryReference {
    return this.withConstraint(startAt(value, key));
  }

  startAfter(value: string | number | boolean | null, key?: string): FirebaseQueryReference {
    return this.withConstraint(startAfter(value, key));
  }

  endAt(value: string | number | boolean | null, key?: string): FirebaseQueryReference {
    return this.withConstraint(endAt(value, key));
  }

  equalTo(value: string | number | boolean | null, key?: string): FirebaseQueryReference {
    return this.withConstraint(equalTo(value, key));
  }

  limitToFirst(limit: number): FirebaseQueryReference {
    return this.withConstraint(limitToFirst(limit));
  }

  limitToLast(limit: number): FirebaseQueryReference {
    return this.withConstraint(limitToLast(limit));
  }

  on(_event: 'value', callback: ValueEventCallback): ValueEventCallback {
    onValue(this.nativeQuery, callback);
    return callback;
  }

  get(): Promise<DataSnapshot> {
    return get(this.nativeQuery);
  }

  once(_event: 'value', callback?: ValueEventCallback): Promise<DataSnapshot> {
    const operation = get(this.nativeQuery);

    if (callback) {
      operation.then(callback);
    }

    return operation;
  }

  toString(): string {
    return this.nativeQuery.toString();
  }

  private withConstraint(constraint: QueryConstraint): FirebaseQueryReference {
    return new FirebaseQueryReference(query(this.nativeQuery, constraint), this.nativeRef);
  }
}

export class FirebaseReference extends FirebaseQueryReference {
  constructor(nativeRef: DatabaseReference) {
    super(nativeRef, nativeRef);
  }

  get key(): string | null {
    return this.nativeRef.key;
  }

  get parent(): FirebaseReference | null {
    return this.nativeRef.parent ? new FirebaseReference(this.nativeRef.parent) : null;
  }

  child(path: string): FirebaseReference {
    return new FirebaseReference(child(this.nativeRef, path));
  }

  set(data: unknown): Promise<void> {
    return set(this.nativeRef, data);
  }

  update(data: object): Promise<void> {
    return update(this.nativeRef, data);
  }

  remove(): Promise<void> {
    return remove(this.nativeRef);
  }

  setPriority(priority: string | number | null, onComplete?: (error: Error | null) => void): Promise<void> {
    const operation = setPriority(this.nativeRef, priority);
    this.notifyOnComplete(operation, onComplete);
    return operation;
  }

  setWithPriority(
    data: unknown,
    priority: string | number | null,
    onComplete?: (error: Error | null) => void,
  ): Promise<void> {
    const operation = setWithPriority(this.nativeRef, data, priority);
    this.notifyOnComplete(operation, onComplete);
    return operation;
  }

  private notifyOnComplete(operation: Promise<void>, onComplete?: (error: Error | null) => void): void {
    if (onComplete) {
      operation.then(
        () => onComplete(null),
        (error: Error) => onComplete(error),
      );
    }
  }
}

export class FirebaseDatabaseReferenceFactory {
  constructor(private readonly nativeDatabase: Database) {}

  ref(path?: string): FirebaseReference {
    return new FirebaseReference(ref(this.nativeDatabase, path));
  }
}

type PathReference = string | DatabaseReference | FirebaseQueryReference;
type QueryFn = (queryRef: FirebaseQueryReference) => FirebaseQueryReference;

@Injectable({
  providedIn: 'root',
})
export class DatabaseWrapper {
  readonly store = inject(Store);

  readonly al = inject(AuditLogService);

  readonly db = getDatabase(getApp(environment.firebase.appId));

  readonly database = new FirebaseDatabaseReferenceFactory(this.db);

  list<T>(pathOrRef: PathReference, queryFn?: QueryFn, teamKey?: string, fromConsole?: boolean): FirebaseList<T> {
    const nativeRef = this.toReference(pathOrRef);
    const queryRef =
      queryFn?.(new FirebaseQueryReference(nativeRef, nativeRef)) ?? new FirebaseQueryReference(nativeRef, nativeRef);
    const path = pathOrRef.toString();

    const list: FirebaseList<T> = {
      query: queryRef,
      valueChanges: () =>
        this.observe(queryRef.nativeQuery, (snapshot) => {
          const values: T[] = [];
          snapshot.forEach((child) => {
            values.push(child.val() as T);
          });
          return values;
        }),
      snapshotChanges: () =>
        this.observe(queryRef.nativeQuery, (snapshot) => {
          const actions: SnapshotAction<T>[] = [];
          snapshot.forEach((child) => {
            actions.push({ key: child.key, payload: child, prevKey: null, type: 'value' });
          });
          return actions;
        }),
      update: (subPath, data) => update(child(nativeRef, subPath.toString()), data),
      set: (subPath, data) => set(child(nativeRef, subPath.toString()), data),
      push: (data) => push(nativeRef, data),
      remove: (subPath) => remove(subPath == null ? nativeRef : child(nativeRef, subPath.toString())),
    };

    if (!teamKey && !this.al.shouldLogAccess()) {
      return list;
    }

    return {
      query: list.query,
      valueChanges: () => this.al.logDataAccess(list.valueChanges(), { access: 'READ', path, teamKey, fromConsole }),
      snapshotChanges: () =>
        this.al.logDataAccess(list.snapshotChanges(), { access: 'READ', path, teamKey, fromConsole }),
      update: (subPath, data) =>
        this.al.logDataAccess(list.update(subPath, data), {
          access: 'UPDATE',
          path: `${path}/${subPath.toString()}`,
          teamKey,
          fromConsole,
        }),
      set: (subPath, data) =>
        this.al.logDataAccess(list.set(subPath, data), {
          access: 'SET',
          path: `${path}/${subPath.toString()}`,
          teamKey,
          fromConsole,
        }),
      remove: (subPath) =>
        this.al.logDataAccess(list.remove(subPath), {
          access: 'REMOVE',
          path: subPath == null ? path : `${path}/${subPath.toString()}`,
          teamKey,
          fromConsole,
        }),
      push: (data) => {
        const pushRef = list.push(data);
        this.al.logDataAccess(
          pushRef.then(() => void 0),
          { access: 'PUSH', path, teamKey, fromConsole },
        );
        return pushRef;
      },
    };
  }

  object<T>(pathOrRef: PathReference, teamKey?: string, fromConsole?: boolean): FirebaseObject<T> {
    const nativeRef = this.toReference(pathOrRef);
    const queryRef = new FirebaseQueryReference(nativeRef, nativeRef);
    const path = pathOrRef.toString();

    const object: FirebaseObject<T> = {
      query: queryRef,
      valueChanges: () => this.observe(nativeRef, (snapshot) => snapshot.val() as T | null),
      snapshotChanges: () =>
        this.observe(nativeRef, (snapshot) => ({
          key: snapshot.key,
          payload: snapshot,
          prevKey: null,
          type: 'value',
        })),
      update: (data) => update(nativeRef, data),
      set: (data) => set(nativeRef, data),
      remove: () => remove(nativeRef),
    };

    if (path.startsWith('/studio') || (!teamKey && !this.al.shouldLogAccess())) {
      return object;
    }

    return {
      query: object.query,
      valueChanges: () => this.al.logDataAccess(object.valueChanges(), { access: 'READ', path, teamKey, fromConsole }),
      snapshotChanges: () =>
        this.al.logDataAccess(object.snapshotChanges(), { access: 'READ', path, teamKey, fromConsole }),
      update: (data) =>
        this.al.logDataAccess(object.update(data), {
          access: 'UPDATE',
          path,
          teamKey,
          data: this.auditData(data),
          fromConsole,
        }),
      set: (data) =>
        this.al.logDataAccess(object.set(data), {
          access: 'SET',
          path,
          teamKey,
          data: this.auditData(data),
          fromConsole,
        }),
      remove: () => this.al.logDataAccess(object.remove(), { access: 'REMOVE', path, teamKey, fromConsole }),
    };
  }

  createPushId(): string {
    return push(ref(this.db)).key as string;
  }

  private observe<T>(queryRef: Query, project: (snapshot: DataSnapshot) => T): Observable<T> {
    return new Observable<T>((subscriber) =>
      onValue(
        queryRef,
        (snapshot) => subscriber.next(project(snapshot)),
        (error) => subscriber.error(error),
      ),
    );
  }

  private toReference(pathOrRef: PathReference): DatabaseReference {
    if (typeof pathOrRef === 'string') {
      return ref(this.db, pathOrRef);
    }

    return pathOrRef instanceof FirebaseQueryReference ? pathOrRef.nativeRef : pathOrRef;
  }

  private auditData(data: unknown): object | string {
    return data !== null && typeof data === 'object' ? data : String(data);
  }
}
